<template>
  <div class="py-4 container-fluid">
    <div class="row">
      <div class="col-12 mb-4">
        <div class="d-flex align-items-center justify-content-between">
          <h5 class="text-white">Mis Tareas Asignadas</h5>
          <span class="badge bg-white text-dark">{{ filteredTasks.length }} Tareas</span>
        </div>
      </div>

      <div class="col-12 mb-4">
        <div class="card shadow-sm border-0">
          <div class="card-body p-3">
            <div class="row">
              <div class="col-md-8 col-12 mb-md-0 mb-3">
                <div class="input-group">
                  <span class="input-group-text text-body border-0 bg-gray-100">
                    <i class="fas fa-search" aria-hidden="true"></i>
                  </span>
                  <input
                    v-model="searchQuery"
                    type="text"
                    class="form-control border-0 bg-gray-100"
                    placeholder="Buscar por descripción..."
                  >
                </div>
              </div>
              <div class="col-md-4 col-12">
                <select v-model="filterType" class="form-select border-0 bg-gray-100">
                  <option value="">Todos los tipos</option>
                  <option v-for="type in uniqueTypes" :key="type" :value="type">
                    {{ type }}
                  </option>
                </select>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div v-if="isLoading" class="col-12 text-center py-5">
        <div class="spinner-border text-white" role="status"></div>
        <p class="text-white mt-2">Cargando tus tareas...</p>
      </div>

      <div v-else class="row">
        <div
          v-for="task in filteredTasks"
          :key="task.id"
          class="col-xl-4 col-md-6 col-12 mb-4"
        >
          <div class="card shadow-sm border-0">
            <div :class="['priority-strip', priorityColor(task.priority)]"></div>

            <div class="card-body p-3">
              <div class="d-flex justify-content-between align-items-start">
                <div>
                  <span class="text-xxs font-weight-bolder text-uppercase text-muted">
                    {{ task.type_name }}
                  </span>
                  <h6 class="mb-0 text-sm d-block">{{ task.task_description }}</h6>
                </div>
                <span :class="['badge badge-sm', priorityBadge(task.priority)]">
                  {{ task.priority }}
                </span>
              </div>

              <hr class="horizontal dark my-2" />

              <div class="row">
                <div class="col-6">
                  <p class="text-xs text-secondary mb-0">Asignado el:</p>
                  <span class="text-xs font-weight-bold">{{ formatDate(task.assigned_at) }}</span>
                </div>
                <div class="col-6 text-end">
                  <p class="text-xs text-secondary mb-0">Fecha Tope:</p>
                  <span :class="['text-xs font-weight-bold', isOverdue(task.due_date) ? 'text-danger' : '']">
                    {{ task.due_date || 'N/A' }}
                  </span>
                </div>
              </div>

              <div class="mt-3 d-flex justify-content-between align-items-center">
                <div class="status-indicator">
                  <i class="fa fa-circle text-xs me-1" :class="statusColor(task.status)"></i>
                  <span class="text-xs">{{ task.status }}</span>
                </div>

                <argon-button
                  v-if="task.status !== 'Completed'"
                  size="sm"
                  color="success"
                  variant="gradient"
                  @click="goToCompleteTask(task.id)"
                >
                  Completar <i class="fa fa-chevron-right ms-1"></i>
                </argon-button>
                <span v-else class="text-secondary text-xs font-weight-bold">
                  <i class="fa fa-check-double me-1"></i> Completada
                </span>
              </div>
            </div>
          </div>
        </div>

        <div v-if="filteredTasks.length === 0" class="col-12 text-center py-5">
          <i class="fa fa-clipboard-check text-white opacity-5 mb-3" style="font-size: 3rem;"></i>
          <p class="text-white">No se encontraron tareas con los filtros aplicados.</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import ArgonButton from "@/components/ArgonButton.vue";
import axios from "axios";
import moment from "moment";

export default {
  name: "MyTasks",
  components: { ArgonButton },
  data() {
    return {
      tasks: [],
      isLoading: true,
      searchQuery: "", // Nuevo
      filterType: "",  // Nuevo
      userId: parseInt(localStorage.getItem('userId')),
      companyId: localStorage.getItem('companyId')
    };
  },
  computed: {
    // 1. Obtenemos tipos únicos de las tareas cargadas para el select
    uniqueTypes() {
      return [...new Set(this.tasks.map(t => t.type_name))].filter(t => t);
    },
    // 2. Aplicamos filtros y luego el ordenamiento original
    filteredTasks() {
      const priorityOrder = { 'High': 1, 'Medium': 2, 'Low': 3 };

      // Filtrado
      let result = this.tasks.filter(task => {
        const matchesSearch = task.task_description.toLowerCase().includes(this.searchQuery.toLowerCase());
        const matchesType = this.filterType === "" || task.type_name === this.filterType;
        return matchesSearch && matchesType;
      });

      // Ordenamiento original
      return result.sort((a, b) => {
        if (priorityOrder[a.priority] !== priorityOrder[b.priority]) {
          return priorityOrder[a.priority] - priorityOrder[b.priority];
        }
        const dateA = a.due_date ? new Date(a.due_date).getTime() : Infinity;
        const dateB = b.due_date ? new Date(b.due_date).getTime() : Infinity;
        if (dateA !== dateB) return dateA - dateB;
        return b.id - a.id;
      });
    }
  },
  methods: {
    async fetchMyTasks() {
      this.isLoading = true;
      const token = localStorage.getItem("accessToken");
      const apiURL = this.$store.state.apiURL;

      try {
        const response = await axios.get(`${apiURL}reports/tasks`, {
          params: {
            type: 'user_detail',
            company_id: this.companyId,
            user_id: this.userId,
            start_date: moment().format('YYYY-MM-DD'), // Ampliado para mejor visibilidad
            end_date: moment().format('YYYY-MM-DD'),
          },
          headers: { 'Authorization': `Bearer ${token}` }
        });
        this.tasks = response.data.filter(task => task.status !== 'Completed' && task.status !== 'Canceled');
      } catch (error) {
        console.error("Error fetching tasks:", error);
      } finally {
        this.isLoading = false;
      }
    },
    priorityColor(priority) {
      if (priority === 'High') return 'bg-danger';
      if (priority === 'Medium') return 'bg-warning';
      return 'bg-info';
    },
    priorityBadge(priority) {
      if (priority === 'High') return 'badge-danger';
      if (priority === 'Medium') return 'badge-warning';
      return 'badge-info';
    },
    statusColor(status) {
      if (status === 'Completed') return 'text-secondary';
      if (status === 'In Progress') return 'text-info';
      return 'text-warning';
    },
    formatDate(date) {
      return moment(date).format('DD MMM, YYYY');
    },
    isOverdue(date) {
      if (!date) return false;
      return moment(date).isBefore(moment(), 'day');
    },
    goToCompleteTask(id) {
      this.$router.push({ name: 'Completar Tarea', params: { id } });
    }
  },
  mounted() {
    this.fetchMyTasks();
  }
};
</script>

<style scoped>
.priority-strip {
  position: absolute;
  left: 0;
  top: 15%;
  bottom: 15%;
  width: 4px;
  border-radius: 0 4px 4px 0;
}
.card {
  transition: transform 0.2s;
  position: relative;
  overflow: hidden;
}
.card:hover {
  transform: translateY(-5px);
}
/* Estilo para los inputs de filtro */
.bg-gray-100 {
  background-color: #f8f9fa !important;
}
</style>